/**
 * useDmlRowEstimate — click-to-arm lifecycle hook behind the DML blast-radius
 * chip (Advisor 2 §A4). Runs the `SELECT COUNT(*)` probe from
 * `buildDmlCountProbeSql` over the existing engine-neutral execution surface
 * (`executeQueryV2` / `cancelQueryV2`) — NO IPC change.
 *
 * Contract:
 *  - **No auto-COUNT.** The chip starts `unarmed`; nothing runs until `arm()`.
 *    A COUNT on a huge un-indexed table is itself expensive, so the user opts in.
 *  - **Arm, then live.** `arm()` runs the probe immediately; while armed it
 *    re-runs **debounced 500 ms** after any change to `probeSql`.
 *  - Each run uses `executionId: 'vqe-probe-' + crypto.randomUUID()` and
 *    `options: { maxRows: 1, timeoutMs: 3000 }`, and passes the tab's `tabId`
 *    so the count runs **inside the tab's active transaction session** — the
 *    estimate reflects uncommitted in-txn state (the correct blast radius).
 *  - The in-flight probe is cancelled (`cancelQueryV2`) on supersede, unmount,
 *    or a null probe. It NEVER blocks or delays Execute — it is suppressed
 *    entirely while a real run is in flight (`isSuppressed`).
 *  - Failure / timeout → `error` state (the chip shows `≈ ?` + a teaching
 *    tooltip). Success fires `feature:vqe_dml_estimate { engine, verb }`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import type { DatabaseEngineId } from '../../../../core/engines';
import type { QueryExecutionResult } from '../../../../types/queryExecution';
import { cancelQueryV2, executeQueryV2 } from '../../../database-objects/services/queryExecutor';

export type DmlEstimateState =
  | { readonly status: 'unarmed' }
  | { readonly status: 'unavailable' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly count: number }
  | { readonly status: 'error' };

export interface UseDmlRowEstimateParams {
  readonly tabId: string;
  readonly connectionId?: string;
  readonly database?: string;
  readonly engineId: DatabaseEngineId;
  readonly verb: 'update' | 'delete';
  /** The COUNT(*) probe SQL, or `null` when the statement shape can't be probed safely. */
  readonly probeSql: string | null;
  /** True while a real execution is in flight — never interleave a probe with it. */
  readonly isSuppressed: boolean;
}

export interface UseDmlRowEstimateResult {
  readonly state: DmlEstimateState;
  /** Arm the estimate (first click) — runs the probe immediately, then keeps it live. */
  readonly arm: () => void;
}

const DEBOUNCE_MS = 500;

export function useDmlRowEstimate(params: UseDmlRowEstimateParams): UseDmlRowEstimateResult {
  const { tabId, connectionId, database, engineId, verb, probeSql, isSuppressed } = params;
  const { track } = useAnalytics();

  const [armed, setArmed] = useState(false);
  const [state, setState] = useState<DmlEstimateState>(
    probeSql === null ? { status: 'unavailable' } : { status: 'unarmed' },
  );

  const inFlightIdRef = useRef<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Analytics latch: while armed, every probeSql change re-runs the probe
  // (debounced 500ms), so tracking each success would fire continuously
  // during live editing (2026-07-25 volume audit long-tail). One event per
  // arm gesture — the chip click IS the usage signal.
  const hasTrackedRef = useRef(false);

  // Latest inputs, so async callbacks / timers never read a stale closure.
  const latest = useRef({ connectionId, database, engineId, verb, probeSql, isSuppressed, tabId, track });
  latest.current = { connectionId, database, engineId, verb, probeSql, isSuppressed, tabId, track };

  const cancelInFlight = useCallback(() => {
    if (inFlightIdRef.current !== null) {
      void cancelQueryV2({ executionId: inFlightIdRef.current });
      inFlightIdRef.current = null;
    }
  }, []);

  const runProbe = useCallback(
    (sql: string) => {
      const l = latest.current;
      if (l.isSuppressed || !l.connectionId) return;
      cancelInFlight();
      const executionId = `vqe-probe-${crypto.randomUUID()}`;
      inFlightIdRef.current = executionId;
      setState({ status: 'loading' });
      void executeQueryV2({
        connectionId: l.connectionId,
        database: l.database,
        sql,
        executionId,
        tabId: l.tabId,
        options: { maxRows: 1, timeoutMs: 3000 },
      }).then((result) => {
        // A superseded / cancelled probe already cleared or replaced the id.
        if (inFlightIdRef.current !== executionId) return;
        inFlightIdRef.current = null;
        const count = extractCount(result);
        if (result.success && count !== null) {
          setState({ status: 'ready', count });
          if (!hasTrackedRef.current) {
            hasTrackedRef.current = true;
            l.track('feature:vqe_dml_estimate', { engine: l.engineId, verb: l.verb });
          }
        } else {
          setState({ status: 'error' });
        }
      });
    },
    [cancelInFlight],
  );

  const arm = useCallback(() => {
    const sql = latest.current.probeSql;
    if (sql === null) return;
    setArmed(true);
    runProbe(sql);
  }, [runProbe]);

  // While armed, re-run the probe (debounced) whenever probeSql changes. While
  // NOT armed, keep the idle display state in sync with probe availability.
  const prevProbeRef = useRef(probeSql);
  useEffect(() => {
    if (!armed) {
      setState(probeSql === null ? { status: 'unavailable' } : { status: 'unarmed' });
      prevProbeRef.current = probeSql;
      return;
    }
    if (probeSql === prevProbeRef.current) return; // arm() already ran this exact SQL
    prevProbeRef.current = probeSql;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (probeSql === null) {
      cancelInFlight();
      setState({ status: 'unavailable' });
      return;
    }
    debounceRef.current = setTimeout(() => runProbe(probeSql), DEBOUNCE_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [probeSql, armed]);

  // Cancel any in-flight probe + pending debounce on unmount.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      cancelInFlight();
    };
  }, [cancelInFlight]);

  return { state, arm };
}

function extractCount(result: QueryExecutionResult): number | null {
  const row = result.resultSets?.[0]?.rows?.[0];
  if (!row) return null;
  const first = Object.values(row)[0];
  if (first === null || first === undefined) return null;
  const n = Number(first as number | bigint | string);
  return Number.isFinite(n) ? n : null;
}
